export const SUPPORT_BUTTON_MODES = {
  HIDDEN: "hidden",
  LEGACY: "legacy",
  V2: "v2",
};

/**
 * Resolves how the support entrypoint should behave for the current project.
 *
 * The returned mode drives both visibility and click behavior:
 * - `v2` opens the external support destination
 * - `legacy` opens the in-app rent support modal
 * - `hidden` does not render the button at all
 *
 * @param {Object} params
 * @param {boolean} params.isCustomerSupportV2Enabled Whether the new support flow is enabled.
 * @param {string} params.area Current project area.
 * @returns {{mode: string, shouldRender: boolean, targetUrl: string}}
 */
export const getSupportButtonConfig = ({ isCustomerSupportV2Enabled, area }) => {
  const supportButtonV2TargetUrl = process.env.REACT_APP_SUPPORT_BUTTON_TARGET_URL || "";

  if (isCustomerSupportV2Enabled && supportButtonV2TargetUrl) {
    return {
      mode: SUPPORT_BUTTON_MODES.V2,
      shouldRender: true,
      targetUrl: supportButtonV2TargetUrl,
    };
  }

  if (area === "rent") {
    return {
      mode: SUPPORT_BUTTON_MODES.LEGACY,
      shouldRender: true,
      targetUrl: "",
    };
  }

  return {
    mode: SUPPORT_BUTTON_MODES.HIDDEN,
    shouldRender: false,
    targetUrl: "",
  };
};

/**
 * Normalizes the inconsistent `customerSupport` payload returned by the project configuration.
 *
 * This helper is intentionally kept outside the store and UI so the application has a single,
 * explicit rule for interpreting all backend variants (`null`, `undefined`, `false`, `{}`,
 * or `{ enabled: boolean }`) as a stable boolean.
 *
 * @param {unknown} customerSupport Raw `projectFlags.customerSupport` payload.
 * @returns {boolean}
 */
export const isCustomerSupportV2Enabled = (customerSupport) => {
  return (
    customerSupport !== null &&
    typeof customerSupport === "object" &&
    customerSupport.enabled === true
  );
};
